import type {FC, PropsWithChildren} from 'react';
import clsx from 'clsx';
import {getImageProps} from 'next/image';

import {Breadcrumb, type BreadcrumbItem} from '@/modules/foundation/components/breadcrumb';
import {Container} from '@/modules/foundation/components/container';
import {Heading} from '@/modules/foundation/components/typography/heading';
import type {PortableTextOverrides} from '@/modules/foundation/sanity/portable-text';

import {getBackgroundImage} from '../getBackgroundImage';

type Props = PropsWithChildren & {
	heading?: string;
	usePattern?: boolean;
	theme?: 'light' | 'dark';
	breadcrumb?: BreadcrumbItem[];
	showBreadcrumb?: boolean;
};

export const textHeadingPortableTextOverrides: PortableTextOverrides = {
	p: 'text-xl',
};

export const TextHeading: FC<Props> = ({
	heading,
	children,
	usePattern = true,
	theme = 'light',
	breadcrumb,
	showBreadcrumb = false,
}) => {
	const {
		props: {srcSet},
	} = getImageProps({alt: '', width: 1960, height: 1024, src: '/images/finkn-pattern.svg'});

	const backgroundImage = getBackgroundImage(srcSet);

	return (
		<div
			className={clsx(
				'relative',
				'overflow-hidden',
				'bg-cover',
				'bg-center',
				theme === 'dark'
					? ['bg-primary-5', 'text-primary-95']
					: ['bg-grey-95', 'border-b', 'border-grey-border'],
			)}
			style={{backgroundImage: usePattern ? backgroundImage : undefined}}
		>
			{breadcrumb && showBreadcrumb && <Breadcrumb breadcrumb={breadcrumb} theme={theme} />}
			<Container className={clsx('py-10')}>
				<div className={clsx('max-w-[630px]')}>
					<Heading size="2xl" spacing={Boolean(children)}>
						{heading}
					</Heading>
					{children && (
						<div
							className={clsx(
								theme === 'dark' ? 'text-secondary-70' : 'text-grey-15',
							)}
						>
							{children}
						</div>
					)}
				</div>
			</Container>
		</div>
	);
};
